import { useState, useMemo, useRef } from "react";
import { Plus, Edit, Trash2, X, AlertCircle, Image, Download, Upload, CheckCircle, XCircle, FileText } from "lucide-react";
import { useAdmin } from "../admin-context";
import MediaPicker from "../MediaPicker";
import { useAppContext } from "../../../context";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import DataTable from "../DataTable";

const PIE_COLORS = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#06b6d4", "#84cc16"];

const SectionCard = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-50">
      <h2 className="text-base font-bold text-gray-800">{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="bg-white rounded-2xl p-5 flex items-center gap-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 ${color}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-800 leading-tight">{value}</p>
      {sub && <p className="text-xs mt-0.5 text-gray-400">{sub}</p>}
    </div>
  </div>
);

// ── CSV helpers ───────────────────────────────────────────────────
const CSV_COLUMNS = ["name", "price", "category", "stock", "lowStockThreshold", "description", "image"];

const parseCSV = (text) => {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  const rows = lines.slice(1).map((line) => {
    const cols = [];
    let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; }
      else if (line[i] === "," && !inQ) { cols.push(cur); cur = ""; }
      else { cur += line[i]; }
    }
    cols.push(cur);
    return Object.fromEntries(headers.map((h, i) => [h, (cols[i] || "").trim()]));
  });
  return { headers, rows };
};

const exportToCSV = (products, categories) => {
  const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name]));
  const header = CSV_COLUMNS.join(",");
  const rows = products.map((p) => {
    const vals = [
      `"${(p.name || "").replace(/"/g, '""')}"`,
      p.price ?? "",
      `"${catMap[p.category_id] || p.category?.name || ""}"`,
      p.stock ?? "",
      p.lowStockThreshold ?? 10,
      `"${(p.description || "").replace(/"/g, '""')}"`,
      `"${p.images?.image || ""}"`,
    ];
    return vals.join(",");
  });
  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `products_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const downloadTemplate = () => {
  const csv = CSV_COLUMNS.join(",") + "\nSample Product,9.99,Drinks,100,10,A delicious product,https://example.com/img.jpg";
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "products_template.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// ── Products page ─────────────────────────────────────────────────
const Products = () => {
  const { products, categories, addProduct, updateProduct, deleteProduct, hasPermission } = useAdmin();
  const { formatPrice } = useAppContext();
  const canCreate = hasPermission("products.create");
  const canEdit   = hasPermission("products.edit");
  const canDelete = hasPermission("products.delete");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [isGalleryPickerOpen, setIsGalleryPickerOpen] = useState(false);

  // Import state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importRows, setImportRows]     = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting]       = useState(false);
  const [importDone, setImportDone]     = useState(false);
  const fileInputRef = useRef(null);

  const catByName = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.name.toLowerCase(), c.id])),
    [categories]
  );

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { rows } = parseCSV(ev.target.result);
      const validated = rows.map((row, i) => {
        const errors = [];
        if (!row.name) errors.push("name required");
        if (!row.price || isNaN(parseFloat(row.price))) errors.push("invalid price");
        if (!row.stock || isNaN(parseInt(row.stock))) errors.push("invalid stock");
        const catId = catByName[row.category?.toLowerCase()];
        if (!catId) errors.push(`unknown category "${row.category}"`);
        return { ...row, _catId: catId, _errors: errors, _index: i + 1 };
      });
      setImportRows(validated);
      setImportErrors(validated.filter((r) => r._errors.length > 0));
      setImportDone(false);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImport = async () => {
    const valid = importRows.filter((r) => r._errors.length === 0);
    setImporting(true);
    for (const row of valid) {
      await addProduct({
        name: row.name,
        price: parseFloat(row.price),
        category_id: row._catId,
        stock: parseInt(row.stock),
        lowStockThreshold: parseInt(row.lowStockThreshold) || 10,
        description: row.description || "",
        images: { image: row.image || "", media_id: null },
        license_id: 2,
      });
    }
    setImporting(false);
    setImportDone(true);
  };

  const closeImport = () => {
    setIsImportOpen(false);
    setImportRows([]);
    setImportErrors([]);
    setImportDone(false);
  };

  const initialForm = {
    name: "", price: "", category_id: "", stock: "",
    lowStockThreshold: 10, image: "", media_id: null, description: "", images: {}, gallery: [],
  };
  const [formData, setFormData] = useState(initialForm);
  const field = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const handleOpen = (product = null) => {
    if (product) {
      setEditingId(product.id);
      setFormData({
        name: product.name, price: product.price,
        category_id: product.category_id, stock: product.stock,
        lowStockThreshold: product.lowStockThreshold || 10,
        images: product.images,
        image: product?.images?.image,
        media_id: product?.images?.media_id || null,
        description: product.description,
        gallery: product?.images?.gallery || [],
      });
    } else {
      setEditingId(null);
      setFormData(initialForm);
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock),
      license_id: 2,
      images: { image: formData.image, media_id: formData.media_id, gallery: formData.gallery },
    };
    delete payload.lowStockThreshold;
    delete payload.image;
    delete payload.media_id;
    delete payload.gallery;

    if (editingId) {
      updateProduct(editingId, payload);
    } else {
      addProduct(payload);
    }
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    deleteProduct(id);
    setDeleteConfirmId(null);
  };

  // ── Chart data derived from real products ─────────────────────
  const topByPrice = useMemo(() =>
    [...products]
      .filter(p => p.price != null)
      .sort((a, b) => parseFloat(b.price) - parseFloat(a.price))
      .slice(0, 7)
      .map(p => ({ name: p.name, price: parseFloat(p.price) }))
  , [products]);

  const byCategory = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const cat = p.category?.name || "Uncategorized";
      map[cat] = (map[cat] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [products]);

  const avgPrice = useMemo(() => {
    if (!products.length) return "—";
    const avg = products.reduce((s, p) => s + parseFloat(p.price || 0), 0) / products.length;
    return `$${avg.toFixed(2)}`;
  }, [products]);

// ── DataTable column definitions ──────────────────────────────
  const columns = [
    {
      key: "name",
      label: "Product",
      sortable: true,
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <img
            src={row.images?.image}
            alt=""
            className="w-11 h-11 rounded-xl object-contain bg-white border p-1 flex-shrink-0"
          />
          <div className="min-w-0">
            <p className="font-bold text-gray-900 truncate max-w-[180px]">{row.name}</p>
            <p className="text-xs text-gray-400">ID: {row.id}</p>
          </div>
        </div>
      ),
    },
    {
      key: "category.name",
      label: "Category",
      sortable: true,
      render: (val) => (
        <span className="bg-gray-100 px-2.5 py-1 rounded-lg text-xs font-bold uppercase text-gray-600">
          {val || "—"}
        </span>
      ),
    },
    {
      key: "stock",
      label: "Stock",
      sortable: true,
      render: (_, row) => {
        const low = row.stock != null && row.stock <= (row.lowStockThreshold || 10);
        return (
          <div className={`flex items-center gap-1.5 font-medium text-sm ${low ? "text-red-600" : "text-gray-700"}`}>
            {row.stock != null ? `${row.stock} units` : "—"}
            {low && <AlertCircle size={13} />}
            {low && (
              <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold">Low</span>
            )}
          </div>
        );
      },
    },
    {
      key: "price",
      label: "Price",
      sortable: true,
      render: (val) => (
        <span className="font-bold text-gray-900">{formatPrice(parseFloat(val || 0))}</span>
      ),
    },
  ];

  // Category filter options derived from live data
  const categoryFilterOptions = categories.map((c) => ({ label: c.name, value: c.name }));

  return (
    <div className="space-y-6">

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="⬡" label="Total Products" value={products.length || "—"} sub="Live from API"      color="bg-indigo-500" />
        <StatCard icon="⚠" label="Low Stock"      value={products.filter(p => p.stock != null && p.stock <= (p.lowStockThreshold || 10)).length} sub="Below threshold" color="bg-amber-500" />
        <StatCard icon="★" label="Categories"     value={categories.length || "—"} sub="Active categories" color="bg-violet-500" />
        <StatCard icon="$" label="Avg. Price"       value={avgPrice}                sub="Across all products"                                                   color="bg-emerald-500" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Top Products by Price">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={topByPrice} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={120} />
              <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, "Price"]} />
              <Bar dataKey="price" fill="#6366f1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>
        <SectionCard title="Products by Category">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={byCategory} cx="50%" cy="50%" outerRadius={100} dataKey="value" nameKey="name" paddingAngle={2}>
                {byCategory.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v} products`, n]} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      {/* Header above table */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Products</h1>
      </div>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={products}
        loading={!products.length}
        searchKeys={["name", "description", "category.name"]}
        searchPlaceholder="Search products..."
        filters={[
          { key: "category.name", label: "Category", options: categoryFilterOptions },
        ]}
        defaultPageSize={10}
        pageSizeOptions={[10, 25, 50]}
        emptyText="No products found. Add your first product."
        headerActions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportToCSV(products, categories)}
              className="border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-xl flex items-center gap-2 font-bold transition active:scale-95 text-sm"
              title="Export products as CSV"
            >
              <Download size={15} /> Export
            </button>
            {canCreate && (
              <button
                onClick={() => setIsImportOpen(true)}
                className="border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-xl flex items-center gap-2 font-bold transition active:scale-95 text-sm"
                title="Import products from CSV"
              >
                <Upload size={15} /> Import
              </button>
            )}
            {canCreate && (
              <button
                onClick={() => handleOpen()}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-bold shadow-lg shadow-orange-200 transition active:scale-95 text-sm"
              >
                <Plus size={16} /> Add Product
              </button>
            )}
          </div>
        }
        actions={(row) => (
          <>
            {canEdit && (
              <button
                onClick={() => handleOpen(row)}
                className="p-2 hover:bg-orange-50 rounded-lg text-orange-500 transition"
                title="Edit"
              >
                <Edit size={15} />
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => setDeleteConfirmId(row.id)}
                className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition"
                title="Delete"
              >
                <Trash2 size={15} />
              </button>
            )}
          </>
        )}
      />

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-5 border-b bg-gray-50">
              <h2 className="text-xl font-bold">{editingId ? "Edit Product" : "New Product"}</h2>
              <button onClick={() => setIsModalOpen(false)} className="hover:bg-gray-200 p-1 rounded-full transition">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left col */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Main Image</label>
                    <button
                      type="button"
                      onClick={() => setIsMediaPickerOpen(true)}
                      className="w-full border-2 border-dashed border-gray-200 hover:border-orange-400 rounded-xl p-3 flex items-center gap-3 transition text-left"
                    >
                      {formData.image ? (
                        <img src={formData.image} alt="" className="w-14 h-14 object-cover rounded-lg flex-shrink-0 border" />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Image size={22} className="text-gray-400" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-700">
                          {formData.image ? "Change image" : "Pick from Media Library"}
                        </p>
                        {formData.image && (
                          <p className="text-xs text-gray-400 truncate">{formData.image}</p>
                        )}
                      </div>
                    </button>
                  </div>

                  {/* Gallery */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">
                      Gallery Images
                      <span className="text-xs font-normal text-gray-400 ml-1">(optional, shown in slider)</span>
                    </label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.gallery.map((img, i) => (
                        <div key={i} className="relative group w-14 h-14 rounded-lg overflow-hidden border flex-shrink-0">
                          <img src={img.url} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setFormData(f => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }))}
                            className="absolute inset-0 bg-black/50 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setIsGalleryPickerOpen(true)}
                        className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-200 hover:border-orange-400 flex items-center justify-center text-gray-400 hover:text-orange-400 transition flex-shrink-0"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Product Name</label>
                    <input
                      required
                      type="text"
                      value={formData.name}
                      onChange={(e) => field("name", e.target.value)}
                      className="w-full border border-gray-200 p-2.5 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    />
                  </div>
                </div>

                {/* Right col */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Price</label>
                      <input
                        required
                        type="number"
                        value={formData.price}
                        onChange={(e) => field("price", e.target.value)}
                        className="w-full border border-gray-200 p-2.5 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
                      <select
                        required
                        value={formData.category_id}
                        onChange={(e) => field("category_id", e.target.value)}
                        className="w-full border border-gray-200 p-2.5 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 bg-white text-sm"
                      >
                        <option value="">Select...</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Stock</label>
                      <input
                        required
                        type="number"
                        value={formData.stock}
                        onChange={(e) => field("stock", e.target.value)}
                        className="w-full border border-gray-200 p-2.5 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1 whitespace-nowrap">Low Stock Alert</label>
                      <input
                        required
                        type="number"
                        value={formData.lowStockThreshold}
                        onChange={(e) => field("lowStockThreshold", e.target.value)}
                        className="w-full border border-gray-200 p-2.5 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Description</label>
                    <textarea
                      required
                      rows={4}
                      value={formData.description}
                      onChange={(e) => field("description", e.target.value)}
                      className="w-full border border-gray-200 p-2.5 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 resize-none text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2 border-t">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit" className="flex-1 py-2.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 shadow-lg shadow-orange-200 transition active:scale-95">
                  {editingId ? "Update Product" : "Create Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={26} className="text-red-500" />
            </div>
            <h2 className="text-lg font-extrabold text-gray-900">Delete Product?</h2>
            <p className="text-sm text-gray-500 mt-2">This will permanently remove the product from your store.</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="flex-1 px-5 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold transition active:scale-95">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {isImportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex justify-between items-center p-5 border-b bg-gray-50">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Import Products</h2>
                <p className="text-xs text-gray-400 mt-0.5">CSV columns: {CSV_COLUMNS.join(", ")}</p>
              </div>
              <button onClick={closeImport} className="hover:bg-gray-200 p-1 rounded-full transition">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              {/* Upload zone */}
              {!importDone && (
                <div
                  className="border-2 border-dashed border-gray-200 hover:border-orange-400 rounded-xl p-8 text-center cursor-pointer transition"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileText size={32} className="text-gray-300 mx-auto mb-3" />
                  <p className="font-bold text-gray-700">Click to select a CSV file</p>
                  <p className="text-xs text-gray-400 mt-1">UTF-8 encoded, comma-separated</p>
                  <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
                </div>
              )}

              {/* Template download */}
              {!importDone && (
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 text-xs text-orange-500 hover:underline font-bold"
                >
                  <Download size={13} /> Download CSV template
                </button>
              )}

              {/* Preview table */}
              {importRows.length > 0 && !importDone && (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-gray-700">
                      Preview — {importRows.length} rows &nbsp;
                      <span className="text-emerald-600">({importRows.length - importErrors.length} valid)</span>
                      {importErrors.length > 0 && (
                        <span className="text-red-500 ml-1">({importErrors.length} with errors)</span>
                      )}
                    </p>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-gray-100">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                        <tr>
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Price</th>
                          <th className="px-3 py-2 text-left">Category</th>
                          <th className="px-3 py-2 text-left">Stock</th>
                          <th className="px-3 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {importRows.map((row) => (
                          <tr key={row._index} className={row._errors.length ? "bg-red-50" : "hover:bg-gray-50"}>
                            <td className="px-3 py-2 text-gray-400">{row._index}</td>
                            <td className="px-3 py-2 font-medium text-gray-800 max-w-[140px] truncate">{row.name || "—"}</td>
                            <td className="px-3 py-2 text-gray-600">{row.price || "—"}</td>
                            <td className="px-3 py-2 text-gray-600">{row.category || "—"}</td>
                            <td className="px-3 py-2 text-gray-600">{row.stock || "—"}</td>
                            <td className="px-3 py-2">
                              {row._errors.length === 0 ? (
                                <span className="flex items-center gap-1 text-emerald-600 font-bold">
                                  <CheckCircle size={12} /> OK
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-red-500 font-bold" title={row._errors.join(", ")}>
                                  <XCircle size={12} /> {row._errors[0]}{row._errors.length > 1 && ` +${row._errors.length - 1}`}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Success state */}
              {importDone && (
                <div className="text-center py-10">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-extrabold text-gray-900">Import Complete</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {importRows.filter((r) => r._errors.length === 0).length} products imported successfully.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-5 border-t bg-gray-50">
              <button onClick={closeImport} className="flex-1 py-2.5 border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-100 transition text-sm">
                {importDone ? "Close" : "Cancel"}
              </button>
              {!importDone && (
                <button
                  onClick={handleImport}
                  disabled={importing || importRows.filter((r) => r._errors.length === 0).length === 0}
                  className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-xl shadow-lg shadow-orange-200 transition active:scale-95 text-sm flex items-center justify-center gap-2"
                >
                  {importing ? (
                    <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Importing...</>
                  ) : (
                    <><Upload size={15} /> Import {importRows.filter((r) => r._errors.length === 0).length} Products</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main image picker */}
      {isMediaPickerOpen && (
        <MediaPicker
          selectedId={formData.media_id}
          onSelect={({ id, url }) => {
            setFormData((f) => ({ ...f, image: url, media_id: id }));
            setIsMediaPickerOpen(false);
          }}
          onClose={() => setIsMediaPickerOpen(false)}
        />
      )}

      {/* Gallery picker (multi-select) */}
      {isGalleryPickerOpen && (
        <MediaPicker
          multiple
          selectedIds={formData.gallery.map(g => g.id)}
          onSelectMultiple={(items) => {
            setFormData((f) => {
              const existingIds = new Set(f.gallery.map(g => g.id));
              const newItems = items.filter(item => !existingIds.has(item.id));
              return { ...f, gallery: [...f.gallery, ...newItems] };
            });
            setIsGalleryPickerOpen(false);
          }}
          onClose={() => setIsGalleryPickerOpen(false)}
        />
      )}

    </div>
  );
};

export default Products;
